import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminHeaderAction,
  AdminFormSecondaryAction,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFormFieldError,
  AdminFormDropdownChevron,
  AdminSupportingText,
  AdminAlertText,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  AdminCompactFormPage,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_FORM_PANEL_CLASS,
  adminFormTwoColGridClass,
} from '../shared/adminUx';
import {
  AVAILABILITY_SCOPE_NOTE,
  BUSINESS_ENTRY_POINT_OPTIONS,
  COMPLIANCE_ASK_MORE_INFO_NOTE,
  CONFIG_STATUS_OPTIONS,
  CUSTOMER_ENTRY_POINT_OPTIONS,
  MESSAGES,
  PRIORITY_OPTIONS,
  SUPPORT_TICKET_CONFIG_ROUTE,
  TICKET_TYPES,
  VISITOR_ENTRY_POINT,
  buildSampleSupportTicketConfigurations,
  collectSupportTicketConfigValidationErrors,
  findSupportTicketConfiguration,
  formatSupportTicketConfigDate,
  getAvailabilityLabel,
  normalizeSupportTicketConfiguration,
} from '../../../data/supportTicketConfig';
import {
  buildSupportTicketConfigActor,
  getVisitorEntryPointDisplay,
  mergeSupportTicketConfigAfterSave,
} from '../../../data/supportTicketConfigWorkflow';

const RequiredMark = () => <span className="text-alertred">*</span>;

const FieldError = ({ error }) =>
  error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null;

const TextValue = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {mono ? (
      <p className="text-offblack text-sm py-2 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="py-2 break-words">{value || '—'}</AdminDetailFieldValue>
    )}
  </div>
);

const ReadOnlyField = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    <input
      type="text"
      value={value || '—'}
      readOnly
      className={`${adminFormDetailInputClasses(true)}${mono ? ' font-mono' : ''}`}
    />
  </div>
);

const AvailabilityToggle = ({ label, checked, onChange, error }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    <label className="relative inline-flex items-center cursor-pointer py-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <div className="relative w-9 h-5 bg-lightgray rounded-full peer-checked:bg-primaryblue after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
      <span className="ml-2 text-sm text-offblack">{getAvailabilityLabel(checked)}</span>
    </label>
    <FieldError error={error} />
  </div>
);

const EntryPointCheckboxes = ({
  label,
  options,
  values,
  onToggle,
  required = false,
  error,
  disabled = false,
}) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>
      {label}
      {required ? (
        <>
          {' '}
          <RequiredMark />
        </>
      ) : null}
    </AdminDetailFieldLabel>
    <div className="space-y-2 py-1">
      {options.map((option) => {
        const checked = (values || []).includes(option.value);
        return (
          <label
            key={option.value}
            className={`flex items-center gap-2 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
          >
            <input
              type="checkbox"
              className="rounded border-lightgray"
              checked={checked}
              disabled={disabled}
              onChange={() => onToggle(option.value)}
            />
            <span className="text-sm text-offblack">{option.label}</span>
          </label>
        );
      })}
    </div>
    <FieldError error={error} />
  </div>
);

const SupportTicketConfigurationDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { configId } = useParams();
  const { auth } = useAuth();
  const actor = useMemo(() => buildSupportTicketConfigActor(auth), [auth]);

  const isEdit = location.pathname.includes('/edit');
  const isView = !isEdit;

  const [configurations] = useState(
    () => location.state?.configurations || buildSampleSupportTicketConfigurations(),
  );

  const baseline = useMemo(() => {
    const fromState = location.state?.configuration
      ? normalizeSupportTicketConfiguration(location.state.configuration)
      : null;
    const fromList = findSupportTicketConfiguration(configurations, configId);
    return fromState?.id === decodeURIComponent(String(configId || ''))
      ? fromState
      : fromList
        ? normalizeSupportTicketConfiguration(fromList)
        : null;
  }, [location.state?.configuration, configurations, configId]);

  const [draft, setDraft] = useState(() =>
    baseline ? normalizeSupportTicketConfiguration(baseline) : null,
  );
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [openedLastUpdated] = useState(() => baseline?.lastUpdated || null);

  useEffect(() => {
    if (baseline) {
      setDraft(normalizeSupportTicketConfiguration(baseline));
      setFieldErrors({});
      setFormError('');
    }
  }, [baseline]);

  const updateDraft = (patch) => {
    setDraft((prev) => normalizeSupportTicketConfiguration({ ...prev, ...patch }));
  };

  const toggleCustomerEntryPoint = (value) => {
    if (!draft?.customerAvailable) return;
    setDraft((prev) => {
      const current = prev?.customerEntryPoints || [];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return normalizeSupportTicketConfiguration({ ...prev, customerEntryPoints: next });
    });
  };

  const toggleBusinessEntryPoint = (value) => {
    if (!draft?.businessAvailable) return;
    setDraft((prev) => {
      const current = prev?.businessEntryPoints || [];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return normalizeSupportTicketConfiguration({ ...prev, businessEntryPoints: next });
    });
  };

  const handleCancel = () => {
    navigate(SUPPORT_TICKET_CONFIG_ROUTE, {
      state: { configurations },
    });
  };

  const handleSave = () => {
    if (!draft || !baseline) {
      setFormError(MESSAGES.notFound);
      return;
    }

    const errors = collectSupportTicketConfigValidationErrors(draft, {
      baseline,
      allConfigurations: configurations,
      openedLastUpdated,
    });
    setFieldErrors(errors);

    const messages = Object.values(errors).filter(Boolean);
    if (messages.length > 0) {
      setFormError(messages.join(' '));
      return;
    }
    setFormError('');

    const result = mergeSupportTicketConfigAfterSave(configurations, draft, actor, {
      openedLastUpdated,
    });
    if (!result.success) {
      setFieldErrors(result.errors || {});
      setFormError(
        Object.values(result.errors || {}).filter(Boolean).join(' ') || result.message,
      );
      return;
    }

    navigate(SUPPORT_TICKET_CONFIG_ROUTE, {
      state: {
        configurations: result.configurations,
        feedback: result.noChange ? undefined : result.message,
      },
    });
  };

  if (!draft) {
    return (
      <AdminLayout>
        <div className="min-w-0">
          <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>
            {isView ? 'Support Ticket Detail' : 'Edit Support Ticket Configuration'}
          </AdminPageTitle>
          <AdminSupportingText className="text-mediumgray">{MESSAGES.notFound}</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  const isGeneralEnquiry = draft.ticketType === TICKET_TYPES.GENERAL_ENQUIRY;
  const validationErrorList = Object.values(fieldErrors).filter(Boolean);
  const customerEntryPoints = (draft.customerEntryPoints || []).join('; ') || '—';
  const businessEntryPoints = (draft.businessEntryPoints || []).join('; ') || '—';

  if (isView) {
    return (
      <AdminLayout>
        <div className="min-w-0">
          <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Support Ticket Detail</AdminPageTitle>

          <div className={`space-y-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
            <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
              <div className={adminFormTwoColGridClass}>
                <TextValue label="ID" value={draft.id} mono />
                <TextValue label="Module" value={draft.module} />
                <TextValue label="Ticket Type" value={draft.ticketType} />
                <TextValue
                  label="Visitor Availability"
                  value={getAvailabilityLabel(draft.visitorAvailable)}
                />
                <TextValue label="Visitor Entry Point" value={getVisitorEntryPointDisplay()} />
                <TextValue
                  label="Customer Availability"
                  value={getAvailabilityLabel(draft.customerAvailable)}
                />
                <TextValue label="Customer Entry Points" value={customerEntryPoints} />
                <TextValue
                  label="Business Availability"
                  value={getAvailabilityLabel(draft.businessAvailable)}
                />
                <TextValue label="Business Entry Points" value={businessEntryPoints} />
                <TextValue label="Priority" value={draft.automaticPriority} />
                <TextValue label="Configuration Status" value={draft.status} />
                <TextValue
                  label="Date Added"
                  value={formatSupportTicketConfigDate(draft.dateAdded)}
                />
                <TextValue
                  label="Last Updated"
                  value={formatSupportTicketConfigDate(draft.lastUpdated)}
                />
              </div>
              {isGeneralEnquiry ? (
                <AdminSupportingText className="text-mediumgray mb-0 mt-4">
                  {COMPLIANCE_ASK_MORE_INFO_NOTE}
                </AdminSupportingText>
              ) : null}
            </DashboardMainContainer>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title="Edit Support Ticket Configuration"
        onSubmit={handleSave}
        showSave={false}
      >
        {/* Section 1 — Configuration Details */}
        <div className="space-y-4 w-full min-w-0">
          <AdminPanelTitle className="text-offblack !mb-0">Configuration Details</AdminPanelTitle>
          <ReadOnlyField label="Support Ticket Configuration ID" value={draft.id} mono />
          <ReadOnlyField label="Module" value={draft.module} />
          <ReadOnlyField label="Ticket Type" value={draft.ticketType} />
        </div>

        {/* Section 2 — Configuration Scope */}
        <div className="space-y-4 w-full min-w-0">
          <AdminPanelTitle className="text-offblack !mb-0">Configuration Scope</AdminPanelTitle>
          <AdminSupportingText className="text-mediumgray mb-0">
            {AVAILABILITY_SCOPE_NOTE}
          </AdminSupportingText>
          {isGeneralEnquiry ? (
            <AdminSupportingText className="text-mediumgray mb-0">
              {COMPLIANCE_ASK_MORE_INFO_NOTE}
            </AdminSupportingText>
          ) : null}

          <AvailabilityToggle
            label="Visitor Availability"
            checked={Boolean(draft.visitorAvailable)}
            onChange={(checked) => updateDraft({ visitorAvailable: checked })}
            error={fieldErrors.visitorAvailable || fieldErrors.submitterAvailability}
          />
          <ReadOnlyField label="Visitor Entry Point" value={VISITOR_ENTRY_POINT} />

          <AvailabilityToggle
            label="Customer Availability"
            checked={Boolean(draft.customerAvailable)}
            onChange={(checked) => updateDraft({ customerAvailable: checked })}
            error={fieldErrors.customerAvailable || fieldErrors.submitterAvailability}
          />
          <EntryPointCheckboxes
            label="Customer Entry Points"
            options={CUSTOMER_ENTRY_POINT_OPTIONS}
            values={draft.customerEntryPoints}
            onToggle={toggleCustomerEntryPoint}
            required={Boolean(draft.customerAvailable)}
            disabled={!draft.customerAvailable}
            error={fieldErrors.customerEntryPoints}
          />

          <AvailabilityToggle
            label="Business Availability"
            checked={Boolean(draft.businessAvailable)}
            onChange={(checked) => updateDraft({ businessAvailable: checked })}
            error={fieldErrors.businessAvailable || fieldErrors.submitterAvailability}
          />
          <EntryPointCheckboxes
            label="Business Entry Points"
            options={BUSINESS_ENTRY_POINT_OPTIONS}
            values={draft.businessEntryPoints}
            onToggle={toggleBusinessEntryPoint}
            required={Boolean(draft.businessAvailable)}
            disabled={!draft.businessAvailable}
            error={fieldErrors.businessEntryPoints}
          />

          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Priority <RequiredMark />
            </AdminDetailFieldLabel>
            <div className="relative w-full min-w-0">
              <select
                className={adminFormDetailSelectClasses(
                  false,
                  Boolean(fieldErrors.automaticPriority),
                )}
                value={draft.automaticPriority || ''}
                onChange={(e) => updateDraft({ automaticPriority: e.target.value })}
              >
                <option value="">Select Priority</option>
                {PRIORITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <AdminFormDropdownChevron />
            </div>
            <FieldError error={fieldErrors.automaticPriority} />
          </div>

          <div className="space-y-1 w-full min-w-0">
            <AdminDetailFieldLabel>
              Configuration Status <RequiredMark />
            </AdminDetailFieldLabel>
            <div className="relative w-full min-w-0">
              <select
                className={adminFormDetailSelectClasses(false, Boolean(fieldErrors.status))}
                value={draft.status || ''}
                onChange={(e) => updateDraft({ status: e.target.value })}
              >
                <option value="">Select Configuration Status</option>
                {CONFIG_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <AdminFormDropdownChevron />
            </div>
            <FieldError error={fieldErrors.status} />
          </div>
        </div>

        {/* Section 3 — Validation Messages */}
        <div className="space-y-2 w-full min-w-0">
          <AdminPanelTitle className="text-offblack !mb-0">Validation Messages</AdminPanelTitle>
          {validationErrorList.length > 0 ? (
            validationErrorList.map((msg) => (
              <AdminAlertText key={msg} className="mb-0">
                {msg}
              </AdminAlertText>
            ))
          ) : formError ? (
            <AdminAlertText className="mb-0">{formError}</AdminAlertText>
          ) : (
            <AdminSupportingText className="text-mediumgray mb-0">
              No validation messages.
            </AdminSupportingText>
          )}
        </div>

        {/* Section 4 — Cancel and Save */}
        <div className="flex items-center justify-end gap-3">
          <AdminFormSecondaryAction type="button" onClick={handleCancel}>
            Cancel
          </AdminFormSecondaryAction>
          <AdminHeaderAction type="submit">Save</AdminHeaderAction>
        </div>
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default SupportTicketConfigurationDetail;
